import type React from 'react';

import type { ColorName } from 'styles/config/colors';

import { cn } from 'utils/cn';
import { colorCssVar } from 'utils/colorCssVar';
import * as Icons from '../Icons';
import type { IconSize } from '../Icons/BaseIcon';

const SIZE_CLASS: Record<string, string> = {
  xxs: 'w-[5px] h-[5px]',
  xs: 'w-xs3 h-xs3',
  sm: 'w-sm1 h-sm1',
  md: 'w-sm2 h-sm2',
  lg: 'w-sm3 h-sm3',
  xl: 'w-sm4 h-sm4',
  xxl: 'w-md1 h-md1',
  xxxl: 'w-md2 h-md2',
};

export interface IconFrameProps {
  type: keyof typeof Icons;
  color?: ColorName;
  backgroundColor?: ColorName;
  size?: keyof typeof IconSize;
}

const IconFrame: React.FC<IconFrameProps> = ({
  type,
  backgroundColor,
  color,
  size = 'xl',
}) => {
  const IconComponent = Icons[type] as React.ComponentType<{
    size?: keyof typeof IconSize;
  }>;

  const cssVars: Record<string, string> = {};
  if (color) cssVars['--icon-color'] = colorCssVar(color);
  if (backgroundColor) cssVars['--icon-bg'] = colorCssVar(backgroundColor);

  return (
    <div
      className={cn(
        'flex shrink-0 gap-xs2 items-center justify-center p-xs2 rounded-full',
        SIZE_CLASS[size],
        color && 'text-[var(--icon-color)]',
        backgroundColor ? 'bg-[var(--icon-bg)]' : 'bg-transparent',
      )}
      style={
        Object.keys(cssVars).length
          ? (cssVars as React.CSSProperties)
          : undefined
      }
    >
      <IconComponent size={size} />
    </div>
  );
};

export default IconFrame;
